import React from 'react';
import { ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import { AbsicherungsCheck } from '../components/AbsicherungsCheck';
import { AbsicherungsBeispiele } from '../components/AbsicherungsBeispiele';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion';
import { Footer } from '../components/legal';

/**
 * Absicherungscheck Seite
 * Eigenständige Seite für den Absicherungs-Check Rechner
 */

const Absicherungscheck = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-ice">
      <Navigation variant="light" />

      {/* Absicherungs-Check Rechner - direkt nach Navigation */}
      <div className="pt-24 sm:pt-28">
        <AbsicherungsCheck />
      </div>

      {/* FAQ Section - direkt unter dem Rechner */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="text-center mb-8 md:mb-12">
            <div className="text-[10px] sm:text-xs uppercase tracking-widest text-slate mb-3">Häufige Fragen</div>
            <h3 className="font-playfair text-xl sm:text-2xl md:text-3xl font-bold text-navy tracking-tight">
              Das werde ich oft gefragt
            </h3>
          </div>
          
          <Accordion type="single" collapsible className="space-y-3">
            {/* 1. Warum ist BU wichtig */}
            <AccordionItem value="item-1" className="bg-bone rounded-xl border border-black/5 shadow-sm px-5 data-[state=open]:shadow-md transition-shadow">
              <AccordionTrigger className="hover:no-underline py-5">
                <span className="font-outfit text-sm sm:text-base font-semibold text-navy text-left pr-4">
                  Warum ist Berufsunfähigkeit so wichtig?
                </span>
              </AccordionTrigger>
              <AccordionContent className="text-xs sm:text-sm text-charcoal leading-relaxed pb-5">
                Statistisch wird jeder Vierte im Laufe seines Erwerbslebens berufsunfähig. Die gesetzliche Erwerbsminderungsrente prüft aber nicht, ob du deinen gelernten Beruf noch ausüben kannst, sondern nur, ob du grundsätzlich noch irgendeiner Tätigkeit auf dem allgemeinen Arbeitsmarkt nachgehen könntest. Eine BU-Versicherung sichert dagegen genau das ab: deinen tatsächlich ausgeübten Beruf.
              </AccordionContent>
            </AccordionItem>
            
            {/* 2. Unterschied Krankentagegeld und BU */}
            <AccordionItem value="item-2" className="bg-bone rounded-xl border border-black/5 shadow-sm px-5 data-[state=open]:shadow-md transition-shadow">
              <AccordionTrigger className="hover:no-underline py-5">
                <span className="font-outfit text-sm sm:text-base font-semibold text-navy text-left pr-4">
                  Was ist der Unterschied zwischen Krankentagegeld und BU?
                </span>
              </AccordionTrigger>
              <AccordionContent className="text-xs sm:text-sm text-charcoal leading-relaxed pb-5">
                Krankentagegeld überbrückt kurzfristige Ausfälle (ab dem 43. Tag bei Angestellten, ab Tag 1 bei Selbstständigen) und zahlt so lange du krank bist. Die BU greift bei dauerhafter Berufsunfähigkeit und zahlt bis zur Rente. Beide ergänzen sich.
              </AccordionContent>
            </AccordionItem>
            
            {/* 3. Risikolebensversicherung */}
            <AccordionItem value="item-3" className="bg-bone rounded-xl border border-black/5 shadow-sm px-5 data-[state=open]:shadow-md transition-shadow">
              <AccordionTrigger className="hover:no-underline py-5">
                <span className="font-outfit text-sm sm:text-base font-semibold text-navy text-left pr-4">
                  Brauche ich eine Risikolebensversicherung?
                </span>
              </AccordionTrigger>
              <AccordionContent className="text-xs sm:text-sm text-charcoal leading-relaxed pb-5">
                Wenn jemand finanziell von dir abhängig ist (Partner, Kinder, laufende Kredite), ist eine Risikoleben wichtig. Sie sichert deine Angehörigen ab, falls dir etwas passiert. Die Beiträge sind oft überraschend günstig.
              </AccordionContent>
            </AccordionItem>
            
            {/* 4. Kosten */}
            <AccordionItem value="item-4" className="bg-bone rounded-xl border border-black/5 shadow-sm px-5 data-[state=open]:shadow-md transition-shadow">
              <AccordionTrigger className="hover:no-underline py-5">
                <span className="font-outfit text-sm sm:text-base font-semibold text-navy text-left pr-4">
                  Was kostet eine gute Absicherung?
                </span>
              </AccordionTrigger>
              <AccordionContent className="text-xs sm:text-sm text-charcoal leading-relaxed pb-5">
                Das hängt von deinem Alter, Beruf und Gesundheitszustand ab. Im Erstgespräch können wir deine individuelle Situation analysieren und dir zeigen, welche Absicherung in deinem Budget möglich ist.
              </AccordionContent>
            </AccordionItem>
            
            {/* 5. Bürojob BU */}
            <AccordionItem value="item-5" className="bg-bone rounded-xl border border-black/5 shadow-sm px-5 data-[state=open]:shadow-md transition-shadow">
              <AccordionTrigger className="hover:no-underline py-5">
                <span className="font-outfit text-sm sm:text-base font-semibold text-navy text-left pr-4">
                  Ich habe einen Bürojob. Brauche ich wirklich eine BU?
                </span>
              </AccordionTrigger>
              <AccordionContent className="text-xs sm:text-sm text-charcoal leading-relaxed pb-5">
                <p className="mb-3">
                  Ja, gerade dann. Denn die häufigsten Ursachen für Berufsunfähigkeit sind keine Unfälle, sondern:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-3 text-slate">
                  <li><span className="text-charcoal">Psychische Erkrankungen</span> (Burnout, Depression) – ca. 30 % aller Fälle</li>
                  <li><span className="text-charcoal">Erkrankungen des Bewegungsapparats</span> (Rücken, Bandscheibe)</li>
                  <li><span className="text-charcoal">Krebs und andere schwere Erkrankungen</span></li>
                </ul>
                <p className="mb-3">
                  Nichts davon hängt davon ab, ob du körperlich arbeitest. Und nichts davon ist deine Schuld.
                </p>
                <p className="text-navy font-medium">
                  Ohne BU-Versicherung bleibt im schlimmsten Fall nur die Erwerbsminderungsrente. Durchschnittlich sind das rund 1.050 € im Monat. Davon Miete, Lebenshaltung, Familie? Fast unmöglich.
                </p>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Fallbeispiele */}
      <AbsicherungsBeispiele />

      {/* Final CTA */}
      <section className="py-16 md:py-24 bg-navy">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 md:px-12 text-center">
          <h2 className="font-playfair text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-6">
            Lücke erkannt?
          </h2>
          <p className="text-white/60 text-base mb-8 max-w-lg mx-auto">
            Im kostenlosen Erstgespräch besprechen wir deine Situation und schauen, 
            wie du die wichtigsten Lücken schließen kannst.
          </p>
          <button
            onClick={() => navigate('/strategiegespraech')}
            className="group inline-flex items-center gap-2 px-6 sm:px-8 py-3.5 bg-white text-navy rounded-xl font-semibold hover:bg-bone transition-all shadow-lg"
          >
            Erstgespräch vereinbaren
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </section>

      <Footer variant="dark" />
    </div>
  );
};

export default Absicherungscheck;
